import {Injectable} from "@angular/core";
import {HttpClient} from "@angular/common/http";
import {BehaviorSubject, Observable} from "rxjs";
import {distinctUntilChanged, map} from "rxjs/operators";
import {IUserInfo, rolesEnum} from "../models/models";
import {environment} from "../../../environments/environment";

export type RoleName = rolesEnum | string;

@Injectable({
  providedIn: 'root'
})

export class RoleService {

  private userSubject = new BehaviorSubject<IUserInfo | null>(null); // Данные пользователя (Users/GetFIO)
  private rolesSubject = new BehaviorSubject<string[]>([]); // Хранит роли пользователя

  user$ = this.userSubject.asObservable();
  roles$ = this.rolesSubject.asObservable(); // Observable для подписки

  constructor(private http: HttpClient) {
    this.restoreFromStorage();
  }

  /**
   * Тянет актуальные роли с сервера и обновляет кэш в localStorage.
   * Вызывается при каждой загрузке страницы (APP_INITIALIZER), чтобы изменения ролей
   * применялись без повторного логина. При любой ошибке откатывается на кэш.
   */
  loadUserInfo(): Promise<IUserInfo | null> {
    const token = localStorage.getItem('token');
    const userName = this.getUserName();

    if (!token || !userName) {
      return Promise.resolve(this.restoreFromStorage());
    }

    return this.http
      .get<IUserInfo>(environment.apiUrl + 'Users/GetFIO', {params: {userName}})
      .toPromise()
      .then(user => {
        if (!user) {
          return this.restoreFromStorage();
        }
        localStorage.setItem('user', JSON.stringify(user));
        this.setUser(user);
        return user;
      })
      .catch(() => this.restoreFromStorage());
  }

  /** Логин пользователя: сохраняется при входе, для старых сессий достаётся из токена */
  getUserName(): string | null {
    const stored = localStorage.getItem('userName');
    if (stored) {
      return stored;
    }

    const fromToken = this.readUserNameFromToken();
    if (fromToken) {
      localStorage.setItem('userName', fromToken);
    }
    return fromToken;
  }

  private readUserNameFromToken(): string | null {
    try {
      const payload = localStorage.getItem('token')?.split('.')[1];
      if (!payload) {
        return null;
      }
      const claims = JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')));
      return claims['userName'] || claims['unique_name'] || claims['sub'] || null;
    } catch {
      return null;
    }
  }

  /** Текущий пользователь */
  get user(): IUserInfo | null {
    return this.userSubject.getValue();
  }

  /** Текущие роли */
  get roles(): string[] {
    return this.rolesSubject.getValue();
  }

  /** Сохраняет пользователя (после getUserInfo) вместе с его ролями */
  setUser(user: IUserInfo | null) {
    this.userSubject.next(user || null);
    this.setRoles(user && user.roles ? user.roles : []);
  }

  // Метод для обновления ролей
  setRoles(roles: string[]) {
    this.rolesSubject.next(Array.isArray(roles) ? roles : []);
  }

  /** Читает пользователя из localStorage — нужно после перезагрузки страницы */
  restoreFromStorage(): IUserInfo | null {
    try {
      const raw = localStorage.getItem('user');
      const user = raw ? JSON.parse(raw) as IUserInfo : null;
      this.setUser(user);
      return user;
    } catch {
      this.setUser(null);
      return null;
    }
  }

  /** Сбрасывает состояние (при logout) */
  clear() {
    this.setUser(null);
  }

  // Метод для проверки наличия роли
  hasRole(role: RoleName): boolean {
    return this.roles.includes(role);
  }

  /** true, если есть хотя бы одна из ролей */
  hasAnyRole(roles: RoleName[]): boolean {
    if (!roles || !roles.length) {
      return false;
    }
    return roles.some(role => this.hasRole(role));
  }

  /** true, если есть все перечисленные роли */
  hasEveryRole(roles: RoleName[]): boolean {
    if (!roles || !roles.length) {
      return false;
    }
    return roles.every(role => this.hasRole(role));
  }

  /** true, если нет ни одной из перечисленных ролей */
  hasNoneOfRoles(roles: RoleName[]): boolean {
    return !this.hasAnyRole(roles);
  }

  /** Реактивная проверка — для async pipe в шаблонах */
  hasRole$(role: RoleName): Observable<boolean> {
    return this.roles$.pipe(
      map(() => this.hasRole(role)),
      distinctUntilChanged()
    );
  }

  /** Реактивная проверка «хотя бы одна из ролей» */
  hasAnyRole$(roles: RoleName[]): Observable<boolean> {
    return this.roles$.pipe(
      map(() => this.hasAnyRole(roles)),
      distinctUntilChanged()
    );
  }
}
